<template>
    <div class="main">
        <h1 class="title is-4">Create a new FD</h1>

        <div v-if="isSuccess" class="notification is-primary align-center">
            <p>FD created successfully!</p>
            <router-link to="/fds">
                <button @click="createFD" class="button is-rounded is-info">Go to FD</button>
            </router-link>
        </div>

        <div v-else style="padding-bottom: 5rem">

            <div ref="errorBox">
                <div v-if="!!error" class="notification is-danger is-light">
                    <p>{{ error }}</p>
                </div>
                <div v-if="validationErrors.length" class="block notification is-danger is-light">
                    <b>Please correct the following error(s):</b>
                    <ol>
                        <li v-for="validationError in validationErrors" :key="validationError">{{ validationError }}</li>
                    </ol>
                </div>
            </div>

            <!-- Date -->
            <div class="box">
                <p class="subtitle is-5 mb-3">Date</p>
                <div class="columns is-mobile">
                    <div class="column is-half-mobile is-half-tablet">
                        <div class="field">
                            <div class="control">
                                <input v-model="date" type="date" class="input"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sponsored -->
            <div class="box has-background-success-light">
                <div class="columns is-mobile is-vcentered mb-2">
                    <div class="column">
                        <p class="subtitle is-5 mb-0">
                            Sponsored
                            <span v-if="isSponsored" class="tag is-info is-light ml-2">Active</span>
                        </p>
                    </div>
                    <div class="column is-narrow">
                        <switch-button
                            label="Sponsored"
                            v-model="isSponsored"
                            @onClick="handleClick($event)">
                        </switch-button>
                    </div>
                </div>

                <div v-if="isSponsored" class="mt-3">
                    <div class="field">
                        <label class="label">Name</label>
                        <div class="control">
                            <input v-model="sponsorName" class="input" type="text" placeholder="Sponsor name">
                        </div>
                    </div>
                    <div class="columns is-mobile">
                        <div class="column is-one-third">
                            <div class="field">
                                <label class="label">Redhill <i class="fas fa-car"></i></label>
                                <div class="control">
                                    <input v-model="drivingVolunteersFromSponsor" class="input" type="number">
                                </div>
                            </div>
                        </div>
                        <div class="column is-one-third">
                            <div class="field">
                                <label class="label">Redhill <i class="fas fa-person-walking"></i></label>
                                <div class="control">
                                    <input v-model="otherVolunteersFromSponsor" class="input" type="number">
                                </div>
                            </div>
                        </div>
                        <div class="column is-one-third">
                            <div class="field">
                                <label class="label">TBR <i class="fas fa-person-walking"></i></label>
                                <div class="control">
                                    <input v-model="tbrVolunteersFromSponsor" class="input" type="number">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Redhill -->
            <div class="box has-background-danger-light">
                <p class="subtitle is-5 mb-3">Redhill</p>
                <div class="columns is-multiline is-mobile">
                    <div class="column is-full">
                        <div class="field">
                            <label class="label">Time</label>
                            <div class="control">
                                <input v-model="time" type="time" class="input"/>
                            </div>
                            <p class="help">Start time for volunteers</p>
                        </div>
                    </div>
                    <div class="column is-half-mobile is-half-tablet">
                        <div class="field">
                            <label class="label"><i class="fas fa-car"></i></label>
                            <div class="control">
                                <input v-model="drivingVolunteers" class="input" type="number">
                            </div>
                        </div>
                    </div>
                    <div class="column is-half-mobile is-half-tablet">
                        <div class="field">
                            <label class="label"><i class="fas fa-person-walking"></i></label>
                            <div class="control">
                                <input v-model="otherVolunteers" class="input" type="number">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TBR -->
            <div class="box has-background-info-light">
                <p class="subtitle is-5 mb-3">TBR</p>
                <div class="columns is-mobile">
                    <div class="column is-half-mobile is-half-tablet">
                        <div class="field">
                            <label class="label">Time</label>
                            <div class="control">
                                <p class="input is-static">{{ tbrTime }}</p>
                            </div>
                            <p class="help">Redhill time + 45 min</p>
                        </div>
                    </div>
                    <div class="column is-half-mobile is-half-tablet">
                        <div class="field">
                            <label class="label"><i class="fas fa-person-walking"></i></label>
                            <div class="control">
                                <input v-model="tbrVolunteers" class="input" type="number">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="sticky-footer">
                <button @click="createFD" class="button is-rounded is-info is-fullwidth is-large" :class="[isLoading ? 'is-loading' : '']">
                    Create FD
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import SwitchButton from '@/components/ui/ToggleSwitch.vue';
import { getDateInYYMMDD } from '@/utils';

export default {
    components: {
        SwitchButton
    },
    data() {
        return {
            isLoading: false,
            isSuccess: false,
            error: null,
            isFormValid: true,
            validationErrors: [],
            date: null,
            time: "10:30",
            drivingVolunteers: null,
            otherVolunteers: null,
            tbrVolunteers: null,
            sponsorName: null,
            isSponsored: false,
            drivingVolunteersFromSponsor: null,
            otherVolunteersFromSponsor: null,
            tbrVolunteersFromSponsor: null
        };
    },
    computed: {
        tbrTime() {
            if (!this.time) return '--:--';
            const [hours, minutes] = this.time.split(':').map(Number);
            const total = hours * 60 + minutes + 45;
            const h = String(Math.floor(total / 60) % 24).padStart(2, '0');
            const m = String(total % 60).padStart(2, '0');
            return `${h}:${m}`;
        }
    },
    methods: {
        async createFD() {
            this.isLoading = true;
            this.validateForm();
            if (!this.isFormValid) {
                this.$nextTick(() => this.$refs.errorBox.scrollIntoView({ behavior: 'smooth' }));
            }
            if (this.isFormValid) {
                try {
                    const [year, month, day] = this.date.split('-');
                    console.log("date input: " + this.date);
                    console.log("Date object:" + new Date(this.date));
                    const fdDate = new Date(Date.UTC(year, month - 1, day, 15, 0, 0)); // 15:00 UTC = 23:00 SGT
                    console.log("fDDate:" + fdDate);
                    await this.$store.dispatch("fd/createFD", {
                        fdID: getDateInYYMMDD(fdDate),
                        fdDate: fdDate,
                        time: this.time,
                        drivingVolunteers: this.drivingVolunteers,
                        otherVolunteers: this.otherVolunteers,
                        tbrVolunteers: this.tbrVolunteers,
                        sponsor: this.sponsorName,
                        sponsorDrivingVolunteers: this.drivingVolunteersFromSponsor,
                        sponsorOtherVolunteers: this.otherVolunteersFromSponsor,
                        sponsorTbrVolunteers: this.tbrVolunteersFromSponsor
                    });
                    this.isSuccess = true;
                }
                catch (error) {
                    this.error = error.message || "Something went wrong!";
                    this.$nextTick(() => this.$refs.errorBox.scrollIntoView({ behavior: 'smooth' }));
                }
            }
            this.isLoading = false;
        },
        validateForm() {
            this.isFormValid = false;
            this.validationErrors = [];
            if (!this.date) {
                this.validationErrors.push("FD Date is required.");
            }
            else {
                const fdDate = new Date(this.date);
                if (fdDate < new Date()) {
                    this.validationErrors.push("FD Date can't be in the past.");
                }
                if (fdDate.getDay() != 6) {
                    this.validationErrors.push("FD Date must be a SATURDAY.");
                }
            }
            if (this.drivingVolunteers < 0 || this.drivingVolunteers == null || this.drivingVolunteers === '') {
                this.validationErrors.push("Driving Volunteers is required and must be >= 0.");
            }
            if (this.otherVolunteers < 0 || this.otherVolunteers == null || this.otherVolunteers === '') {
                this.validationErrors.push("Walking Volunteers is required and must be >= 0.");
            }
            if (this.tbrVolunteers < 0 || this.tbrVolunteers == null || this.tbrVolunteers === '') {
                this.validationErrors.push("TBR Walking Volunteers is required and must be >= 0.");
            }
            if (this.isSponsored) {
                if (!this.sponsorName) {
                    this.validationErrors.push("Sponsor's Name is required.");
                }
                if (this.drivingVolunteersFromSponsor < 0 || this.drivingVolunteersFromSponsor == null || this.drivingVolunteersFromSponsor === '') {
                    this.validationErrors.push("Redhill Driving Volunteers from sponsor is required and must be >= 0.");
                }
                if (this.otherVolunteersFromSponsor < 0 || this.otherVolunteersFromSponsor == null || this.otherVolunteersFromSponsor === '') {
                    this.validationErrors.push("Redhill Walking Volunteers from sponsor is required and must be >= 0.");
                }
                if (this.tbrVolunteersFromSponsor < 0 || this.tbrVolunteersFromSponsor == null || this.tbrVolunteersFromSponsor === '') {
                    this.validationErrors.push("TBR Walking Volunteers from sponsor is required and must be >= 0.");
                }
            }
            if (!this.validationErrors.length) {
                this.isFormValid = true;
            }
        },
        handleClick(isChecked) {
            this.isSponsored = isChecked;
            if (this.isSponsored) {
                this.drivingVolunteers = 0;
                this.otherVolunteers = 0;
                this.tbrVolunteers = 0;
            } else {
                this.sponsorName = null;
                this.drivingVolunteersFromSponsor = null;
                this.otherVolunteersFromSponsor = null;
                this.tbrVolunteersFromSponsor = null;
            }
        }
    }
}
</script>